import type {
  ChallengeImageTypeApi,
  ChallengeStatusApi,
} from 'api-typings/challenges/challenges';
import {
  ChallengeElementSourceTypeApi,
  ChallengeUiPropertyTypeApi,
} from 'api-typings/challenges/challenges';
import type { Contest } from '../contests';
import type { Timeline } from '../timelines';

/**
 * A Challenge is an entity type that contains details about the overall challenge,
 * including a description and custom banners to be displayed.
 * Challenges will be one-to-many mapped to contest elements.
 */

export interface Challenge {
  readonly id: number;
  readonly userId: number;
  readonly title: string;
  readonly subtitle?: string;
  readonly description: string;
  readonly prize?: number;
  readonly enterpriseId?: number;
  readonly enterpriseInternalName?: string;
  readonly landingPageUrl?: string;
  readonly images: ChallengeImages;
  readonly elements: readonly ChallengeElement[];
  readonly currencyId?: number;
  readonly status: ChallengeStatusApi;
  readonly rankOrder: number;
  readonly timePublished: number;
  readonly timeUpdated: number;
  readonly timeDeleted?: number;
  readonly uiProperties: ChallengeUiProperties;
  /** This determines which endpoint should be called on an update datastore request */
  readonly action?: 'rankOrder' | 'update';
  /**
   * HACK: The `challenges_admin_rank_order_update` endpoint expects an array of challenge IDs to determine the rank orders.
   * Therefore this field is required since the datastore only supports updating by partial document type.
   */
  readonly rankedChallengeIds?: readonly number[];
}

export interface ChallengeUiProperties {
  readonly bannerTitleColor: string;
  readonly bannerSubtitleColor: string;
  readonly cardTitleColor: string;
  readonly cardSubtitleColor: string;
}

export interface ChallengeImage {
  readonly height: number;
  readonly type: ChallengeImageTypeApi;
  readonly url?: string;
  /** The Pelican UUID of the image */
  readonly fileId?: string;
  readonly width: number;
}

export interface ChallengeImages {
  readonly card?: ChallengeImage;
  readonly bannerMobile?: ChallengeImage;
  readonly bannerDesktop?: ChallengeImage;
  readonly logo?: ChallengeImage;
  readonly seo?: ChallengeImage;
}

export type ChallengeElement =
  | ChallengeContestElement
  | ChallengeUnlinkedElement;

export interface ChallengeContestElement extends ChallengeElementBase {
  readonly sourceId: number;
  readonly sourceType: ChallengeElementSourceTypeApi.CONTEST;
  readonly contestDetails?: Contest;
  readonly timelineDetails?: Timeline;
}

export interface ChallengeUnlinkedElement extends ChallengeElementBase {
  readonly sourceType: ChallengeElementSourceTypeApi.UNLINKED;
}

interface ChallengeElementBase {
  readonly id: number;
  readonly rankOrder: number;
  readonly name: string;
}

export function isChallengeContestElement(element: {
  readonly sourceType?: ChallengeElementSourceTypeApi;
}): element is ChallengeContestElement {
  return element.sourceType === ChallengeElementSourceTypeApi.CONTEST;
}

export function isChallengeElement(element: any): element is ChallengeElement {
  return (
    typeof element.name === 'string' &&
    (element.sourceType === ChallengeElementSourceTypeApi.CONTEST ||
      element.sourceType === ChallengeElementSourceTypeApi.UNLINKED)
  );
}

export function isChallengeUiPropertyTypeApi(
  property: string,
): property is ChallengeUiPropertyTypeApi {
  return Object.values(ChallengeUiPropertyTypeApi).includes(
    property as ChallengeUiPropertyTypeApi,
  );
}
